import axios, { AxiosResponse } from 'axios';
import VersionedResource from '../VersionedResource';
import { KeymapClaimField, KeymapClaimFieldResponse } from './KeymapClaimField';
import { KeymapConfigHelper, KeymapConfigHelperResponse } from './KeymapConfigHelpers';
import { KeymapConfig, KeymapConfigResults, KeymapConfigResult } from './KeymapConfig';
import {
 KeymapConfigEvaluationResult, KeymapEvaluateInput, KeymapConfigResultError, KeymapConfigResultSuccess, KeymapConfigVerifyResult,
 KeymapVerifyInput, KeymapConfigVerifyResultError, KeymapConfigVerifyResultSuccess,
} from './KeymapConfigResult';

export default class RCXKeymapConfigResource extends VersionedResource {
  baseUrl = '/api/rcx/config';

  public async getAllClaimFields(): Promise<KeymapClaimField[]> {
    const url = `${this.baseUrl}/keymap-claim-field/all`;

    const response: AxiosResponse<KeymapClaimFieldResponse> = await this.apiClient.get(
      url,
    );
    return response.data.data;
  }

  public async getHelpers(): Promise<KeymapConfigHelper[]> {
    const url = `${this.baseUrl}/keymap-config-scripts`;

    const response: AxiosResponse<KeymapConfigHelperResponse> = await this.apiClient.get(
      url,
    );
    return response.data.data.helpers;
  }

  public async get(fid: number): Promise<KeymapConfig[]> {
    const url = `${this.baseUrl}/${fid}/keymap-config/all`;

    const response: AxiosResponse<KeymapConfigResults> = await this.apiClient.get(
      url,
    );
    return response.data.data;
  }

  public async create(keymapConfig: KeymapConfig): Promise<KeymapConfig> {
    const { fiId } = keymapConfig;
    const url = `${this.baseUrl}/${fiId}/keymap-config`;

    const response: AxiosResponse<KeymapConfigResult> = await this.apiClient.post(
      url,
      keymapConfig,
    );
    return response.data.data;
  }

  public async update(keymapConfig: KeymapConfig): Promise<KeymapConfig> {
    const { fiId, id } = keymapConfig;
    const url = `${this.baseUrl}/${fiId}/keymap-config/${id}`;

    const response: AxiosResponse<KeymapConfigResult> = await this.apiClient.put(
      url,
      keymapConfig,
    );
    return response.data.data;
  }

  public async evaluate(keymapConfig: KeymapEvaluateInput): Promise<KeymapConfigEvaluationResult> {
    const { fiId } = keymapConfig;

    const url = `${this.baseUrl}/${fiId}/keymap-config-evaluate`;

    try {
      const response: AxiosResponse<KeymapConfigEvaluationResult> = await this.apiClient.post(
        url,
        keymapConfig,
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if ('data' in error.response && this.isKeymapConfigResultError(error.response.data)) {
            return error.response.data
          }
        }
      }
      throw error;
    }
  }

  public async verify(keymapVerifyInput: KeymapVerifyInput): Promise<KeymapConfigVerifyResult> {
    const { fiId } = keymapVerifyInput;

    const url = `${this.baseUrl}/${fiId}/keymap-verify`;

    try {
      const response: AxiosResponse<KeymapConfigVerifyResult> = await this.apiClient.post(
        url,
        keymapVerifyInput,
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if ('data' in error.response && this.isKeymapVerifyError(error.response.data)) {
            return error.response.data
          }
        }
      }
      throw error;
    }
  }

  public isKeymapConfigResultError(obj: any): obj is KeymapConfigResultError {
    return !!obj?.message && !!obj?.type
  }

  public isKeymapConfigResultSuccess(obj: any): obj is KeymapConfigResultSuccess {
    return !!obj?.result
  }

  public isKeymapVerifyError(obj: any): obj is KeymapConfigVerifyResultError {
    return !!obj?.error
  }

  public isKeymapVerifySuccess(obj: any): obj is KeymapConfigVerifyResultSuccess {
    return !!obj?.matches
  }
}
